export interface SaleorCropCoordinates {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

export interface CropRectangle {
  height: number;
  left: number;
  top: number;
  width: number;
}

export interface ImageDimensions {
  height: number;
  width: number;
}

export const DEFAULT_STRAP_REFERENCE_DIMENSIONS: ImageDimensions = {
  height: 1024,
  width: 768,
};

export interface StrapPreviewMediaCandidate {
  strapPreviewCoordinates?: string | null;
  url?: string | null;
}

export const DEFAULT_STRAP_PREVIEW_COORDS: SaleorCropCoordinates = {
  x1: 415,
  x2: 800,
  y1: 840,
  y2: 900,
};

const hasNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const getFileNameFromUrl = (src: string): string => {
  const withoutQuery = src.split('?')[0] ?? src;
  const withoutHash = withoutQuery.split('#')[0] ?? withoutQuery;
  const pathSegments = withoutHash.split('/');
  return pathSegments[pathSegments.length - 1] ?? withoutHash;
};

export const selectStrapPreviewMedia = <T extends StrapPreviewMediaCandidate>(
  media: T[] | null | undefined,
): T | null => {
  if (!media?.length) {
    return null;
  }

  const mediaWithCoordinates = media.find(
    (item) =>
      hasNonEmptyString(item.url) &&
      hasNonEmptyString(item.strapPreviewCoordinates),
  );
  if (mediaWithCoordinates) {
    return mediaWithCoordinates;
  }

  const soldierMedia = media.find(
    (item) => hasNonEmptyString(item.url) && /(?:-|_)soldier_/i.test(item.url),
  );
  if (soldierMedia) {
    return soldierMedia;
  }

  const mediaWithUrl = media.find((item) => hasNonEmptyString(item.url));
  if (mediaWithUrl) {
    return mediaWithUrl;
  }

  return media[0] ?? null;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const buildNormalizedCoordinates = (
  rawCoordinates: Partial<SaleorCropCoordinates>,
): SaleorCropCoordinates | null => {
  const rawX1 = toFiniteNumber(rawCoordinates.x1);
  const rawX2 = toFiniteNumber(rawCoordinates.x2);
  const rawY1 = toFiniteNumber(rawCoordinates.y1);
  const rawY2 = toFiniteNumber(rawCoordinates.y2);

  if (rawX1 === null || rawX2 === null || rawY1 === null || rawY2 === null) {
    return null;
  }

  const left = Math.min(rawX1, rawX2);
  const right = Math.max(rawX1, rawX2);
  const top = Math.min(rawY1, rawY2);
  const bottom = Math.max(rawY1, rawY2);

  if (right <= left || bottom <= top) {
    return null;
  }

  return {
    x1: left,
    x2: right,
    y1: top,
    y2: bottom,
  };
};

const normalizeFallbackCoordinates = (
  fallback: SaleorCropCoordinates,
): SaleorCropCoordinates =>
  buildNormalizedCoordinates(fallback) ?? DEFAULT_STRAP_PREVIEW_COORDS;

export const normalizeSaleorCropCoordinates = (
  coordinates: Partial<SaleorCropCoordinates> | null | undefined,
  fallback: SaleorCropCoordinates = DEFAULT_STRAP_PREVIEW_COORDS,
): SaleorCropCoordinates => {
  const normalizedFallback = normalizeFallbackCoordinates(fallback);

  if (!coordinates) {
    return normalizedFallback;
  }

  const normalized = buildNormalizedCoordinates(coordinates);
  if (!normalized) {
    return normalizedFallback;
  }

  return normalized;
};

export const parseSaleorCropCoordinates = (
  coordinatesRaw: string | null | undefined,
  fallback: SaleorCropCoordinates = DEFAULT_STRAP_PREVIEW_COORDS,
): SaleorCropCoordinates => {
  if (!coordinatesRaw) {
    return normalizeSaleorCropCoordinates(null, fallback);
  }

  try {
    const parsed = JSON.parse(coordinatesRaw) as Partial<SaleorCropCoordinates>;
    return normalizeSaleorCropCoordinates(parsed, fallback);
  } catch {
    return normalizeSaleorCropCoordinates(null, fallback);
  }
};

export const toCropRectangle = (
  coordinates: SaleorCropCoordinates,
): CropRectangle => ({
  height: coordinates.y2 - coordinates.y1,
  left: coordinates.x1,
  top: coordinates.y1,
  width: coordinates.x2 - coordinates.x1,
});

export const toAkamaiCropTransform = (crop: CropRectangle): string =>
  `Crop,rect=(${crop.left},${crop.top},${crop.width},${crop.height})`;

export const toCloudflareTrimOptions = (crop: CropRectangle): string[] => [
  `trim.left=${crop.left}`,
  `trim.top=${crop.top}`,
  `trim.width=${crop.width}`,
  `trim.height=${crop.height}`,
];

export const normalizeImageDimensions = (
  dimensions: Partial<ImageDimensions> | null | undefined,
): ImageDimensions | null => {
  if (!dimensions) {
    return null;
  }

  const width = toFiniteNumber(dimensions.width);
  const height = toFiniteNumber(dimensions.height);

  if (width === null || height === null || width <= 0 || height <= 0) {
    return null;
  }

  return {
    height,
    width,
  };
};

export const inferSourceDimensionsFromUrl = (
  src: string,
  referenceDimensions: ImageDimensions | null,
): ImageDimensions | null => {
  if (!src || !referenceDimensions) {
    return null;
  }

  // Saleor thumbnail path: /thumbnail/{base64_id}/{size}/{format}/
  const saleorPathMatch = src.match(
    /\/thumbnail\/[^/]+\/(\d+)\/(?:webp|jpe?g|png|avif)\/?(?:$|\?|#)/i,
  );
  if (saleorPathMatch?.[1]) {
    const size = Number(saleorPathMatch[1]);
    if (Number.isFinite(size) && size > 0) {
      return { height: size, width: size };
    }
  }

  const fileName = getFileNameFromUrl(src);
  if (!fileName) {
    return null;
  }

  const saleorThumbnailSizeMatch = fileName.match(
    /_thumbnail_(\d+)\.(?:jpe?g|png|webp|avif)(?:$|\?)/i,
  );
  if (saleorThumbnailSizeMatch?.[1]) {
    const thumbnailSize = Number(saleorThumbnailSizeMatch[1]);
    if (Number.isFinite(thumbnailSize) && thumbnailSize > 0) {
      return {
        height: thumbnailSize,
        width: thumbnailSize,
      };
    }
  }

  const sizeTokenMatch = fileName.match(
    /_(\d+)\.(?:jpe?g|png|webp|avif)(?:$|\?)/i,
  );
  if (!sizeTokenMatch?.[1]) {
    return null;
  }

  const inferredWidth = Number(sizeTokenMatch[1]);
  if (!Number.isFinite(inferredWidth) || inferredWidth <= 0) {
    return null;
  }

  const inferredHeight = Math.round(
    inferredWidth * (referenceDimensions.height / referenceDimensions.width),
  );

  if (!Number.isFinite(inferredHeight) || inferredHeight <= 0) {
    return null;
  }

  return {
    height: inferredHeight,
    width: inferredWidth,
  };
};

export const scaleCropRectangle = (
  crop: CropRectangle,
  fromDimensions: ImageDimensions,
  toDimensions: ImageDimensions,
): CropRectangle => {
  const scaleX = toDimensions.width / fromDimensions.width;
  const scaleY = toDimensions.height / fromDimensions.height;

  return {
    height: Math.round(crop.height * scaleY),
    left: Math.round(crop.left * scaleX),
    top: Math.round(crop.top * scaleY),
    width: Math.round(crop.width * scaleX),
  };
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const clampCropRectangleToBounds = (
  crop: CropRectangle,
  bounds: ImageDimensions,
): CropRectangle => {
  if (bounds.width <= 0 || bounds.height <= 0) {
    return crop;
  }

  const left = clamp(Math.round(crop.left), 0, Math.max(bounds.width - 1, 0));
  const top = clamp(Math.round(crop.top), 0, Math.max(bounds.height - 1, 0));
  const maxWidth = Math.max(bounds.width - left, 1);
  const maxHeight = Math.max(bounds.height - top, 1);
  const width = clamp(Math.round(crop.width), 1, maxWidth);
  const height = clamp(Math.round(crop.height), 1, maxHeight);

  return {
    height,
    left,
    top,
    width,
  };
};

export const buildCloudflareStrapFallbackCrop = (
  dimensions: ImageDimensions,
): CropRectangle => {
  const strapSize = Math.max(
    48,
    Math.round(Math.min(dimensions.width, dimensions.height) * 0.15),
  );

  return clampCropRectangleToBounds(
    {
      height: strapSize,
      left: Math.round(dimensions.width * 0.42),
      top: Math.round(dimensions.height * 0.722),
      width: strapSize,
    },
    dimensions,
  );
};

export const buildCloudflareStrapModalCrop = (
  dimensions: ImageDimensions,
): CropRectangle => {
  const cropWidth = Math.max(120, Math.round(dimensions.width * 0.19));
  const cropHeight = Math.max(56, Math.round(dimensions.height * 0.0625));

  return clampCropRectangleToBounds(
    {
      height: cropHeight,
      left: Math.round(dimensions.width * 0.402),
      top: Math.round(dimensions.height * 0.818),
      width: cropWidth,
    },
    dimensions,
  );
};

export const shouldScaleCropRectangle = (
  crop: CropRectangle,
  referenceDimensions: ImageDimensions,
  sourceDimensions: ImageDimensions,
): boolean => {
  if (
    sourceDimensions.width === referenceDimensions.width &&
    sourceDimensions.height === referenceDimensions.height
  ) {
    return false;
  }

  // Saleor coordinates can be slightly outside expected bounds due to rounding.
  const REFERENCE_SPACE_TOLERANCE_FACTOR = 1.1;
  const maxReferenceRight =
    referenceDimensions.width * REFERENCE_SPACE_TOLERANCE_FACTOR;
  const maxReferenceBottom =
    referenceDimensions.height * REFERENCE_SPACE_TOLERANCE_FACTOR;

  const right = crop.left + crop.width;
  const bottom = crop.top + crop.height;

  return (
    crop.left >= 0 &&
    crop.top >= 0 &&
    right <= maxReferenceRight &&
    bottom <= maxReferenceBottom
  );
};
